/**
 * Periodically fetches /version.json and reloads the page when
 * the build timestamp changes (i.e. a new deployment happened).
 *
 * Call `startVersionCheck()` once at app bootstrap.
 * Only runs in production (`process.env.NODE_ENV === 'production'`).
 */

interface VersionInfo {
  buildTime: string;
  commit: string | null;
}

const POLL_INTERVAL_MS = 60_000; // 60 seconds
const VERSION_URL = '/version.json';

let initialVersion: VersionInfo | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store' });

    if (!response.ok) return null;

    const data: VersionInfo = await response.json();
    return data;
  } catch {
    // Silently ignore network errors
    return null;
  }
}

async function checkForUpdate(): Promise<void> {
  const latest = await fetchVersion();

  if (!latest || !initialVersion) return;

  if (latest.buildTime !== initialVersion.buildTime) {
    console.info('[versionCheck] New version detected, reloading…');
    window.location.reload();
  }
}

export async function startVersionCheck(): Promise<void> {
  if (process.env.NODE_ENV !== 'production') return;

  initialVersion = await fetchVersion();

  if (!initialVersion) return;

  intervalId = setInterval(checkForUpdate, POLL_INTERVAL_MS);
}

export function stopVersionCheck(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
